// feedback.* — user-initiated free-form feedback to the operator.
//
// Anyone connected over WS can submit free-text feedback through the UI
// (the "Takamin window", `launch-rollout-plan.md` §3.3). Submissions are
// stored server-side and read out-of-band by the operator. There is no
// in-app response loop — the form UI says "no individual reply, ping us
// on X if you need one" — so `feedback.ack` / `feedback.rejected` are
// purely transport-level outcomes, not application replies.
//
// Sender-only unicast on response (no broadcast, no server-stamped
// `uid`/`seq`/`ts`; this is a command/event-split shape similar to
// admin.kick).
import { z } from 'zod';
/**
 * `feedback.submit` — c→s.
 *
 * Free-form message, optionally tagged with the room the user is in at
 * submission time (purely contextual; the server doesn't broadcast or
 * surface this anywhere user-visible).
 *
 * Permission: none. Anyone can submit; abuse is contained via the
 * server-side rate limit (= same uid, 5 submissions per 24h) and the
 * upper bound on `message` length.
 */
export const feedbackSubmitSchema = z.object({
    t: z.literal('feedback.submit'),
    /** Free-form text, 1..5000 chars (server re-validates). */
    message: z.string().min(1).max(5000),
    /** Optional room id the user is currently in. */
    roomId: z.string().optional(),
});
/**
 * `feedback.ack` — s→c unicast back to the sender on successful storage.
 *
 * Carries the stored record id purely so the UI has a stable handle to
 * show ("送信しました") — not a foothold for any further interaction.
 */
export const feedbackAckSchema = z.object({
    t: z.literal('feedback.ack'),
    /** Server-assigned uuid of the stored feedback row. */
    id: z.string(),
});
/**
 * `feedback.rejected` — s→c unicast back to the sender on failure.
 *
 * Reasons:
 * - `rate-limited` — same uid already submitted 5 times in the last 24h
 * - `message-too-long` — `message` longer than 5000 chars (defensive;
 *   client schema also caps this, so this means a non-conforming client)
 * - `message-empty` — `message` is empty / whitespace only
 */
export const feedbackRejectedSchema = z.object({
    t: z.literal('feedback.rejected'),
    reason: z.enum(['rate-limited', 'message-too-long', 'message-empty']),
    message: z.string().optional(),
});
//# sourceMappingURL=feedback.js.map